/**
 * Swing Pivot 支撐壓力（純函式，L1 技術層，無 LLM）。
 *
 * 跟「當日高低 / ATR 預估 / 布林軌」不同 —— 這裡找的是**歷史轉折點**（fractal swing
 * high/low）：價格真的在那裡反轉過，才是有意義的支撐壓力，不是統計帶。
 *
 * 流程：
 *   1. fractal 偵測：第 i 根的 high 嚴格高於左右各 `leftRight` 根的 high → swing high（壓力候選）；
 *      low 嚴格低於左右各 `leftRight` 根的 low → swing low（支撐候選）。
 *   2. 聚類：把相近價位的 pivot 合併成一條「帶」（容差用 ATR 自適應，拿不到才退回價%），
 *      price 取觸碰點均值、touches=觸碰次數、lastIndex=最後觸碰（新近度）。
 *   3. 強度：strength = 觸碰次數權重 + 新近度權重，正規化到 0..1。
 *
 * 回傳「依強度 desc 排序」的 levels；前端再依現價分上下方各取 N 條畫線。
 */
import { atr } from './atr.js';
/** fractal 偵測：回傳所有 swing high/low 原始轉折點（用 wick high/low）。 */
function findSwingPivots(candles, leftRight) {
    const pivots = [];
    for (let i = leftRight; i < candles.length - leftRight; i++) {
        const h = candles[i].high;
        const l = candles[i].low;
        let isHigh = true;
        let isLow = true;
        for (let j = i - leftRight; j <= i + leftRight; j++) {
            if (j === i)
                continue;
            if (candles[j].high >= h)
                isHigh = false;
            if (candles[j].low <= l)
                isLow = false;
            if (!isHigh && !isLow)
                break;
        }
        if (isHigh)
            pivots.push({ idx: i, price: h, kind: 'high' });
        if (isLow)
            pivots.push({ idx: i, price: l, kind: 'low' });
    }
    return pivots;
}
/**
 * 從 candles 算出 swing 支撐壓力帶。資料不足（< 2*leftRight+1 根）回空陣列。
 */
export function computeSupportResistance(candles, options = {}) {
    const leftRight = options.leftRight ?? 2;
    const valid = candles.filter((c) => [c.high, c.low, c.close].every((v) => typeof v === 'number' && Number.isFinite(v)));
    if (valid.length < leftRight * 2 + 1)
        return [];
    const lastClose = valid[valid.length - 1].close;
    const currentPrice = options.currentPrice ?? lastClose;
    if (!(currentPrice > 0))
        return [];
    // 聚類容差：優先 ATR 自適應（不同價位股票自動縮放），退回價% / 0.6%。
    let tolerance;
    if (options.tolerancePct != null) {
        tolerance = currentPrice * (options.tolerancePct / 100);
    }
    else {
        const a = atr(valid.map((c) => c.high), valid.map((c) => c.low), valid.map((c) => c.close), 14);
        tolerance = a != null && a > 0 ? a * 0.6 : currentPrice * 0.006;
    }
    if (!(tolerance > 0))
        tolerance = currentPrice * 0.006;
    const pivots = findSwingPivots(valid, leftRight);
    if (pivots.length === 0)
        return [];
    // 聚類：價位排序後，相鄰差距 < tolerance 的歸同一帶。
    const sorted = [...pivots].sort((a, b) => a.price - b.price);
    const clusters = [];
    for (const p of sorted) {
        const last = clusters[clusters.length - 1];
        if (last && p.price - last.prices[last.prices.length - 1] <= tolerance) {
            last.prices.push(p.price);
            last.touches += 1;
            last.lastIndex = Math.max(last.lastIndex, p.idx);
        }
        else {
            clusters.push({ prices: [p.price], touches: 1, lastIndex: p.idx });
        }
    }
    const n = valid.length;
    const maxTouches = Math.max(...clusters.map((c) => c.touches));
    const levels = clusters.map((c) => {
        const price = c.prices.reduce((a, b) => a + b, 0) / c.prices.length;
        // 強度：觸碰次數（0.6 權重）+ 新近度（0.4 權重，最後觸碰越靠近末端越高）。
        const touchScore = c.touches / maxTouches;
        const recencyScore = n > 1 ? c.lastIndex / (n - 1) : 1;
        const strength = Math.min(1, touchScore * 0.6 + recencyScore * 0.4);
        return {
            price,
            type: price >= currentPrice ? 'resistance' : 'support',
            touches: c.touches,
            strength,
            lastIndex: c.lastIndex,
        };
    });
    return levels.sort((a, b) => b.strength - a.strength);
}
/**
 * 取現價上方最近 N 條壓力 + 下方最近 N 條支撐（畫線用）。
 * 「最近」＝離現價最近的價位（不是最強），讓圖上看到的是「下一個會碰到的」關卡。
 */
export function nearestSupportResistance(levels, currentPrice, perSide = 2) {
    const resistance = levels
        .filter((l) => l.price > currentPrice)
        .sort((a, b) => a.price - b.price)
        .slice(0, perSide);
    const support = levels
        .filter((l) => l.price < currentPrice)
        .sort((a, b) => b.price - a.price)
        .slice(0, perSide);
    return { resistance, support };
}
/**
 * 突破確認遲滯（hysteresis）分類：判定一個價位「目前」是支撐還是壓力，
 * **等收盤確實突破、且超過 buffer 才翻轉角色**（價格只是觸及 / 假突破不翻）。
 *
 * 用收盤序列（K 棒歷史本身就是記憶，不需 React state）確定性地算：
 *   - 收盤 > pivot + buffer → 這個價位被「漲破」→ 之後它是支撐（撐在下方）
 *   - 收盤 < pivot - buffer → 這個價位被「跌破」→ 之後它是壓力（壓在上方）
 *   - 在 [pivot-buffer, pivot+buffer] 內 → 維持上一個角色（遲滯，碰一下不翻）
 * seed 用第一根收盤的幾何位置。
 */
export function classifyByBreakout(pivotPrice, closes, buffer) {
    if (closes.length === 0)
        return 'resistance';
    let role = pivotPrice >= closes[0] ? 'resistance' : 'support';
    for (const c of closes) {
        if (!Number.isFinite(c))
            continue;
        if (c > pivotPrice + buffer)
            role = 'support';
        else if (c < pivotPrice - buffer)
            role = 'resistance';
    }
    return role;
}
/**
 * 一站式：從 candles 算 swing 支撐壓力，並用「突破確認遲滯」分類角色，
 * 取每側離現價最近 perSide 條。給技術帶 / 大 K 線 / 迷你 K 共用，三處一致。
 *
 * 與 `nearestSupportResistance` 的差別：後者用「現價在上方=壓力」硬切（碰到就翻）；
 * 這個用收盤突破確認（超過 ATR×0.3 緩衝才翻），避免價格在關卡附近震盪時標記跳動。
 */
export function stickySupportResistance(candles, currentPrice, perSide = 2) {
    const valid = candles.filter((c) => [c.high, c.low, c.close].every((v) => typeof v === 'number' && Number.isFinite(v)));
    if (valid.length === 0 || !(currentPrice > 0))
        return { resistance: [], support: [] };
    const levels = computeSupportResistance(valid, { currentPrice });
    if (levels.length === 0)
        return { resistance: [], support: [] };
    const closes = valid.map((c) => c.close);
    // buffer：ATR×0.3 自適應（不同價位股票自動縮放），退回現價 0.4%。
    const a = atr(valid.map((c) => c.high), valid.map((c) => c.low), closes, 14);
    const buffer = a != null && a > 0 ? a * 0.3 : currentPrice * 0.004;
    const classified = levels.map((l) => ({ ...l, type: classifyByBreakout(l.price, closes, buffer) }));
    const byNearest = (x, y) => Math.abs(x.price - currentPrice) - Math.abs(y.price - currentPrice);
    const resistance = classified
        .filter((l) => l.type === 'resistance')
        .sort(byNearest)
        .slice(0, perSide)
        .sort((a2, b2) => a2.price - b2.price); // 顯示：壓力由近到遠（價升序）
    const support = classified
        .filter((l) => l.type === 'support')
        .sort(byNearest)
        .slice(0, perSide)
        .sort((a2, b2) => b2.price - a2.price); // 顯示：支撐由近到遠（價降序）
    return { resistance, support };
}
//# sourceMappingURL=pivots.js.map